module Meevo.Components.Controls {
    "use strict";

    interface IAddressLookupScope extends angular.IScope {
        /** i18n key for the field label. */
        contentKey: string;
        /** Address model to populate: { country, address1, address2, city, state, zipCode }. */
        model: any;
        /** State dropdown options (shared with the sibling state field); repopulated on selection. */
        states: Array<{ state: string }>;

        searchValue: string;
        suggestions: any[];
        showSuggestions: boolean;

        onFocus(): void;
        onSearchChanged(): void;
        selectSuggestion(suggestion: any): void;
    }

    // An additive typeahead that queries Radar's address autocomplete (like the Adyen hosted page)
    // and maps the selected address directly into the bound model + reloads the state options.
    export class AddressLookup implements angular.IDirective {
        restrict = "E";
        scope = {
            contentKey: "@",
            model: "=",
            states: "=?"
        };
        template = `
            <style>
                .address-lookup__field { position: relative; }
                .address-lookup__suggestions { position: absolute; top: 100%; left: 0; right: 0; z-index: 1000; background: #fff; border: 1px solid #d5c8ec; border-top: none; box-shadow: 0 2px 6px rgba(0,0,0,.15); max-height: 280px; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
                .address-lookup__suggestion { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #f0f0f0; }
                .address-lookup__suggestion:last-child { border-bottom: none; }
                .address-lookup__suggestion:hover { background: #f3eefb; }
                .address-lookup__empty { padding: 8px 12px; color: #9aa5ab; cursor: default; user-select: none; }
            </style>
            <div class="address-lookup">
                <div class="meevo_input_wrapper">
                    <label translate="{{contentKey}}"></label>
                </div>
                <div class="address-lookup__field">
                    <input type="text"
                           ng-model="searchValue"
                           ng-model-options="{ debounce: 300 }"
                           ng-change="onSearchChanged()"
                           ng-focus="onFocus()"
                           placeholder="{{ 'LABEL_SEARCH' | translate }}"
                           autocomplete="off" />
                    <ul class="address-lookup__suggestions" ng-if="showSuggestions">
                        <li class="address-lookup__suggestion"
                            ng-repeat="s in suggestions"
                            ng-click="selectSuggestion(s)">{{ s.formattedAddress }}</li>
                        <li class="address-lookup__empty" ng-if="suggestions.length === 0" translate="LABEL_NO_OPTIONS_FOUND"></li>
                    </ul>
                </div>
            </div>`;

        constructor(
            private appSettingsConfigService: Meevo.Components.Common.IAppSettingsConfigService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private addressFieldsService: Meevo.Components.Common.IAddressFieldsService
        ) {
        }

        link = (scope: IAddressLookupScope, element: angular.IAugmentedJQuery) => {
            let apiKey: string;
            let apiBaseUrl: string;
            let docClickHandler: (e: any) => void;

            scope.suggestions = [];
            scope.showSuggestions = false;

            this.appSettingsConfigService.getConfig().then(config => {
                apiKey = config && config.geolocationApiKey;
                apiBaseUrl = ((config && config.geolocationApiBaseUrl) || '').replace(/\/+$/, '');
            });

            const closeDropdown = () => {
                scope.searchValue = '';
                scope.suggestions = [];
                scope.showSuggestions = false;
                if (docClickHandler) {
                    $(document).off('click', docClickHandler);
                    docClickHandler = null;
                }
            };

            // Open the dropdown as soon as the field is focused (shows "No options found" until
            // enough is typed for results); close it when the user clicks outside the control.
            scope.onFocus = () => {
                scope.showSuggestions = true;
                if (!docClickHandler) {
                    docClickHandler = (e: any) => {
                        if (!element[0].contains(e.target)) {
                            scope.$applyAsync(closeDropdown);
                        }
                    };
                    $(document).on('click', docClickHandler);
                }
            };

            scope.onSearchChanged = () => {
                const value = scope.searchValue;
                if (!value || value.length < 3 || !apiKey) {
                    // Keep the dropdown open showing "No options found" until there are matches.
                    scope.suggestions = [];
                    return;
                }
                this.search(scope, apiKey, apiBaseUrl, value);
            };

            scope.selectSuggestion = (suggestion: any) => {
                this.applyAddress(scope, suggestion);
                // closeDropdown() clears the input/suggestions; the fields now hold the address.
                closeDropdown();
            };

            scope.$on('$destroy', closeDropdown);
        };

        // Map the selected Radar address directly into the bound model.
        private applyAddress(scope: IAddressLookupScope, address: any) {
            const model = scope.model;
            if (!model) {
                return;
            }

            const countryCode = address.countryCode;
            const isUsCa = countryCode === 'US' || countryCode === 'CA';
            // Map to an existing Meevo country, else "Other".
            const meevoCountry = this.appSettings.APP_COUNTRIES[countryCode] ? countryCode : 'OTHER';

            model.country = meevoCountry;
            // US/CA: house number + street on the address line; other countries: addressLabel.
            model.address1 = isUsCa
                ? [address.number, address.street].filter(Boolean).join(' ')
                : (address.addressLabel || address.street);
            model.address2 = address.unit || '';
            model.city = address.city;
            model.zipCode = address.postalCode;

            // Reload the state options for the country (shared with the sibling state field),
            // then set the state code for US/CA (unset otherwise).
            this.addressFieldsService.getStates(meevoCountry).then(list => {
                if (scope.states) {
                    scope.states.length = 0;
                    Array.prototype.push.apply(scope.states, list || []);
                }
                model.state = isUsCa ? address.stateCode : undefined;
            });
        }

        // Radar best practice for US/CA checkout autocomplete: layers=address, countryCode=US,CA.
        // Uses fetch (not $http) so Meevo's request interceptors don't touch the external Radar call.
        private search(scope: IAddressLookupScope, apiKey: string, apiBaseUrl: string, value: string) {
            const country = scope.model && scope.model.country;
            const searchCountryCode = (country === 'US' || country === 'CA') ? 'US,CA' : country;
            let query = 'query=' + encodeURIComponent(value) + '&layers=address';
            if (searchCountryCode) {
                query += '&countryCode=' + encodeURIComponent(searchCountryCode);
            }

            fetch(apiBaseUrl + '/search/autocomplete?' + query, { headers: { 'Authorization': apiKey } })
                .then(res => res.ok ? res.json() : { addresses: [] })
                .then(data => {
                    scope.$applyAsync(() => {
                        scope.suggestions = (data && data.addresses) || [];
                        // Show the dropdown even with no results so "No options found" is visible.
                        scope.showSuggestions = true;
                    });
                })
                .catch(() => {
                    scope.$applyAsync(() => {
                        scope.suggestions = [];
                        scope.showSuggestions = true;
                    });
                });
        }

        static factory(): angular.IDirectiveFactory {
            let directive = (
                appSettingsConfigService: Meevo.Components.Common.IAppSettingsConfigService,
                appSettings: Meevo.Components.Common.IAppSettings,
                addressFieldsService: Meevo.Components.Common.IAddressFieldsService
            ) => new AddressLookup(appSettingsConfigService, appSettings, addressFieldsService);

            directive.$inject = [
                "meevo.components.common.AppSettingsConfigService",
                "meevo.AppSettings",
                "meevo.components.common.AddressFieldsService"
            ];
            return directive;
        }
    }

    angular.module("meevo.components.controls")
        .directive("meevoAddressLookup", AddressLookup.factory());
}
